/* Phase 5 · HR-Lite — Daily attendance
 * P/A/H/L tap-list, inspector auto-derive, override with reason.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["P", "A", "H", "L"];
const badgeStyle = {
  P: "bg-emerald-100 text-emerald-800",
  A: "bg-red-100 text-red-700",
  H: "bg-amber-100 text-amber-800",
  L: "bg-purple-100 text-purple-800",
  auto: "bg-blue-100 text-blue-800",
  leave: "bg-purple-100 text-purple-800",
};

export default function DailyAttendance() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState({ entries: [] });
  const [dirty, setDirty] = useState({});           // staff_id -> {status, override_reason}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/hr/attendance", { params: { date } });
    setData(r.data);
    setDirty({});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const mark = (staff_id, status) => {
    setDirty(d => ({ ...d, [staff_id]: { ...(d[staff_id] || {}), status } }));
  };
  const reason = (staff_id, r) => setDirty(d => ({ ...d, [staff_id]: { ...(d[staff_id] || {}), override_reason: r } }));

  const save = async () => {
    const entries = Object.entries(dirty)
      .filter(([, v]) => v.status)
      .map(([staff_id, v]) => ({ staff_id, status: v.status, override_reason: v.override_reason }));
    if (entries.length === 0) return toast.error("Nothing to save");
    setSaving(true);
    try {
      await api.post("/hr/attendance/mark", { date, entries });
      toast.success(`Marked ${entries.length}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl" data-testid="hr-attendance">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">HR</div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0B2340]">Daily Attendance</h1>
        </div>
        <input type="date" className="ml-auto border rounded px-2 py-1.5 text-sm"
          data-testid="hr-att-date"
          value={date} onChange={e => setDate(e.target.value)} />
      </div>

      <p className="text-xs text-[#7C8E9E] mb-3">
        Inspectors are auto-marked <span className="font-mono">P</span> if they synced ≥1 inspection today. Override with reason if needed.
      </p>

      <div className="bg-white rounded-lg border border-slate-100 overflow-hidden">
        {data.entries.length === 0 ? (
          <div className="p-4 text-sm text-[#7C8E9E]">No staff yet — add one in Staff Master.</div>
        ) : data.entries.map(e => {
          const d = dirty[e.staff_id] || {};
          const current = d.status || e.status;
          return (
            <div key={e.staff_id} className="border-b border-slate-50 p-3 flex items-center justify-between gap-2 flex-wrap"
              data-testid={`hr-att-row-${e.staff_id}`}>
              <div className="min-w-[140px]">
                <div className="font-medium text-sm">{e.name}</div>
                <div className="text-[10px] text-[#7C8E9E] uppercase">{e.role_title} · {e.source}</div>
              </div>
              <div className="flex gap-1">
                {STATUSES.map(s => (
                  <button key={s} onClick={() => mark(e.staff_id, s)}
                    data-testid={`hr-att-${e.staff_id}-${s}`}
                    className={`w-9 h-9 rounded-full text-sm font-bold ${current === s ? badgeStyle[s] + " ring-2 ring-[#0B2340]" : "bg-slate-100 text-slate-500"}`}>
                    {s}
                  </button>
                ))}
              </div>
              {d.status && d.status !== e.status && (
                <input placeholder="Reason for override" className="border rounded px-2 py-1 text-xs w-full md:w-64"
                  data-testid={`hr-att-${e.staff_id}-reason`}
                  value={d.override_reason || ""} onChange={ev => reason(e.staff_id, ev.target.value)} />
              )}
            </div>
          );
        })}
      </div>

      <button onClick={save} disabled={saving} data-testid="hr-att-save"
        className="mt-4 px-5 py-2 rounded-full bg-[#B08B49] text-white text-sm disabled:opacity-50">
        {saving ? "Saving…" : "Save marks"}
      </button>
    </div>
  );
}
